import React, { useState, useEffect } from "react";
import { productsAPI } from "../../utils/api";
import ProductsData from "../../data/products/ProductsData.json"; // Fallback
import { Link } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";

const PortfolioOne = ({ Column }) => {
  const [getAllItems, setGetAllItems] = useState([]);
  const [dataVisibleCount, setDataVisibleCount] = useState(6);
  const [dataIncrement] = useState(6);
  const [noMorePost, setNoMorePost] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await productsAPI.getAll();

        if (!Array.isArray(data)) {
          console.error('[PortfolioOne] API returned non-array data:', data);
          throw new Error('Invalid data format from API');
        }

        setGetAllItems(data);
      } catch (error) {
        console.error('[PortfolioOne] Error fetching products:', error);
        console.error('[PortfolioOne] Error details:', {
          message: error.message,
          stack: error.stack
        });
        // Fallback to JSON data only if API completely fails
        console.warn('[PortfolioOne] Falling back to JSON data');
        setGetAllItems(ProductsData);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  useEffect(() => {
    if (getAllItems.length > 0) {
      // Use slice instead of filter by ID - this shows products by array position, not ID
      setVisibleItems(getAllItems.slice(0, dataVisibleCount));
      setNoMorePost(dataVisibleCount >= getAllItems.length);
    }
  }, [getAllItems, dataVisibleCount]);

  const handleLoadmore = (e) => {
    e.preventDefault();
    const newCount = Math.min(dataVisibleCount + dataIncrement, getAllItems.length);
    setDataVisibleCount(newCount);
    setNoMorePost(newCount >= getAllItems.length);
  };

  if (loading) {
    return (
      <div className="row row--15">
        <div className="col-lg-12 text-center" style={{ padding: '50px' }}>
          <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  // Use same-origin on live so product images load (build-time env may be localhost)
  const API_URL = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
    ? window.location.origin
    : (process.env.REACT_APP_API_URL || 'http://localhost:3005').replace(/\/api\/?$/, '').replace(/\/+$/, '');

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces, underscores, and multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  // Helper function to construct image URL
  const getImageUrl = (imageEntry) => {
    if (!imageEntry) return null;
    
    let imageUrl = '';
    if (typeof imageEntry === 'string') {
      imageUrl = imageEntry;
    } else if (typeof imageEntry === 'object') {
      imageUrl = imageEntry.url || imageEntry.path || '';
    }
    
    if (!imageUrl) return null;
    
    // If already a full URL, return as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // If relative path starting with /uploads/, prepend API_URL
    if (imageUrl.startsWith('/uploads/')) {
      return `${API_URL}${imageUrl}`;
    }
    if (!imageUrl.startsWith('/')) {
      return `${API_URL}/uploads/product/${imageUrl}`;
    }
    
    return imageUrl;
  };

  // Component to handle image with error fallback
  const PortfolioImage = ({ src, alt }) => {
    const [imageError, setImageError] = useState(false);
    
    if (!src || imageError) {
      return (
        <div className="portfolio-placeholder">
          <span>No Image</span>
        </div>
      );
    }
    
    return (
      <img 
        src={src} 
        alt={alt}
        onError={() => setImageError(true)}
      />
    );
  };

  if (visibleItems.length === 0 && !loading) {
    return (
      <div className="row row--15">
        <div className="col-lg-12 text-center" style={{ padding: '50px' }}>
          <p>No products found. Create products in the admin panel.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="row row--15">
        {visibleItems.map((item) => {
          const images = Array.isArray(item.images) ? item.images : [];
          const firstImageEntry = images.length > 0 ? images[0] : null;
          
          const firstImage = getImageUrl(firstImageEntry);
          const firstAlt = firstImageEntry?.alt || item.title || 'Product image';
          
          const productSlug = item.slug || generateSlug(item.title) || item.id;
          
          return (
            <div key={item.id} className={Column || "col-lg-6 col-md-6 col-sm-12 col-12 mt--30 portfolio"}>
              <Link 
                to={`${(process.env.PUBLIC_URL || "").replace(/\/$/, "")}/product-nodes/${productSlug}`}
                className="portfolio"
              >
                <div className="rn-portfolio" data-sal="slide-up" data-sal-delay="150" data-sal-duration="800">
                  <div className="inner">
                    <div className="thumbnail">
                      <PortfolioImage src={firstImage} alt={firstAlt} />
                    </div>
                    <div className="content">
                      <div className="category-info">
                        <div className="category-list">
                          <span>{item.type || 'Product'}</span>
                        </div>
                      </div>
                      <h4 className="title">{item.title}</h4>
                      {item.description && (
                        <p className="description">{item.description.substring(0, 100)}...</p>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          );
        })}
      </div>

      {getAllItems.length > 0 && (
        <div className="row row--15">
          <div className="col-lg-12">
            <div className="rwt-load-more text-center mt--50">
              <button
                className="btn btn-default btn-large btn-icon"
                onClick={handleLoadmore}
                disabled={noMorePost}
              >
                {noMorePost ? (
                  "No More Items"
                ) : (
                  <span>
                    Load More
                    <span className="icon">
                      <FaSpinner />
                    </span>
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PortfolioOne;
